import { getLocSamplesForMap, logEvent } from './debugLog';
import { getPois } from './poiService';
import { loadConfig } from './configService';
import { getLang, t } from '../i18n';
import type { Vec3 } from '../types';

/**
 * Localization Coach (AI agent 2): when tracking struggles — slow first fix
 * or a Relocalizing stretch — turn the venue's OWN localization history into
 * one actionable instruction ("point toward the Kitchen, ~3 m to your left,
 * where tracking usually locks on").
 *
 * How: the on-device heatmap samples (recorded on every successful VPS fix)
 * are grid-clustered into "good zones" (high confidence, multiple fixes);
 * the nearest zones get a direction word relative to the visitor's heading
 * and the nearest POI name as a landmark. Claude (loc-coach Edge Function,
 * Haiku) phrases the instruction in the visitor's language; without the
 * ANTHROPIC_API_KEY secret a deterministic template says the same thing.
 * Self-throttled (45 s cooldown, max 3 per session) — a coach that nags is
 * worse than no coach.
 */

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const COOLDOWN_MS = 45_000;
const MAX_PER_SESSION = 3;
const CELL_M = 1.5;

let lastCoachAt = 0;
let coachCount = 0;

export interface CoachZone {
  x: number;
  z: number;
  conf: number;
  count: number;
  distM?: number;
  dir?: string;
  landmark?: string;
}

/** Grid-cluster the heatmap samples into high-confidence zones. */
function goodZones(): CoachZone[] {
  const cells = new Map<string, { sx: number; sz: number; sc: number; n: number }>();
  // Scoped to the ACTIVE venue only — samples are device-wide in storage
  // (see debugLog.ts LocSample.map), so an unscoped read on a multi-venue
  // test device mixes unrelated map-space coordinates into one cluster,
  // which is why coaching used to feel generic/repetitive.
  for (const s of getLocSamplesForMap(loadConfig().code)) {
    const k = `${Math.round(s.x / CELL_M)}:${Math.round(s.z / CELL_M)}`;
    const c = cells.get(k) ?? { sx: 0, sz: 0, sc: 0, n: 0 };
    c.sx += s.x;
    c.sz += s.z;
    c.sc += s.c;
    c.n++;
    cells.set(k, c);
  }
  return [...cells.values()]
    .map((c) => ({ x: c.sx / c.n, z: c.sz / c.n, conf: c.sc / c.n, count: c.n }))
    .filter((z) => z.conf >= 0.7 && z.count >= 2)
    .sort((a, b) => b.conf * Math.min(b.count, 10) - a.conf * Math.min(a.count, 10))
    .slice(0, 3);
}

/** Direction word for a zone relative to the visitor's heading (map space). */
function dirWord(from: Vec3, headingRad: number, zone: CoachZone): string {
  const bearing = Math.atan2(zone.x - from.x, -(zone.z - from.z));
  let rel = bearing - headingRad;
  while (rel > Math.PI) rel -= 2 * Math.PI;
  while (rel < -Math.PI) rel += 2 * Math.PI;
  const deg = (rel * 180) / Math.PI;
  if (Math.abs(deg) <= 45) return t('coach.ahead');
  if (Math.abs(deg) >= 135) return t('coach.behind');
  return deg > 0 ? t('coach.right') : t('coach.left');
}

/**
 * Produce one coaching line (or null when throttled). Position/heading may
 * be null (e.g. first fix never happened) — zones then have no direction.
 */
export async function coachLocalization(
  kind: 'first-fix' | 'relocalizing',
  position: Vec3 | null,
  headingRad: number | null,
): Promise<string | null> {
  const now = Date.now();
  if (now - lastCoachAt < COOLDOWN_MS || coachCount >= MAX_PER_SESSION) return null;
  lastCoachAt = now;
  coachCount++;

  // Enrich the best zones with distance/direction/landmark.
  const zones = goodZones();
  if (position) {
    for (const z of zones) {
      z.distM = Math.hypot(z.x - position.x, z.z - position.z);
      if (headingRad !== null) z.dir = dirWord(position, headingRad, z);
    }
    zones.sort((a, b) => (a.distM ?? 1e9) - (b.distM ?? 1e9));
  }
  try {
    const pois = await getPois();
    for (const z of zones) {
      let bestD = 4; // landmark only when a POI is genuinely nearby
      for (const p of pois) {
        const d = Math.hypot(p.position.x - z.x, p.position.z - z.z);
        if (d < bestD) {
          bestD = d;
          z.landmark = p.name;
        }
      }
    }
  } catch {
    /* no catalog — zones stay unnamed */
  }

  // Deterministic line (also the fallback when the LLM isn't configured).
  // Tiered: distance+direction needs a live position (never true on the
  // 'first-fix' trigger — the visitor isn't localized yet by definition),
  // but the nearest-landmark lookup doesn't need position at all. Without
  // this middle tier, every first-fix coaching line fell straight to the
  // fully generic string regardless of the venue's own history.
  const best = zones[0];
  const fallback =
    best && best.distM !== undefined && best.dir
      ? t('coach.zone', {
          landmark: best.landmark ?? t('coach.thatArea'),
          m: Math.max(1, Math.round(best.distM)),
          dir: best.dir,
        })
      : best?.landmark
        ? t('coach.zoneLandmark', { landmark: best.landmark })
        : t('coach.generic');

  let reply = fallback;
  let source = 'lite';
  if (SUPABASE_URL) {
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/loc-coach`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          lang: getLang(),
          kind,
          hasPosition: position !== null,
          zones: zones.map((z) => ({
            distM: z.distM,
            dir: z.dir,
            conf: z.conf,
            count: z.count,
            landmark: z.landmark,
          })),
        }),
      });
      if (res.ok) {
        const data = (await res.json()) as { reply?: string };
        if (data.reply) {
          reply = data.reply;
          source = 'llm';
        }
      }
    } catch {
      /* offline / not configured — fallback already set */
    }
  }
  logEvent('session', `coach (${source}, ${kind}): "${reply.slice(0, 80)}"`);
  return reply;
}

/** Reset throttling (call on AR session start). */
export function resetCoach(): void {
  lastCoachAt = 0;
  coachCount = 0;
}
